import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "./api";

const AuthCtx = createContext(null);

/**
 * Auth context — relies on httpOnly cookie for the actual token (set by the
 * backend on /auth/login & /auth/register). We only persist a small non-sensitive
 * user object (id, email, name, role) in localStorage for UI hydration speed.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem("psg_user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Network/server errors here aren't user-actionable — the local session
      // is cleared either way. We log for diagnostics so silent failures are
      // still observable in dev tools / error reporters.
      console.warn("Logout request failed (clearing local session anyway):", err);
    }
    localStorage.removeItem("psg_user");
    setUser(null);
  }, []);

  // Validate session on mount. We always attempt to hydrate from the server
  // — even when localStorage is empty — because the source of truth is the
  // httpOnly `psg_token` cookie, not local state. A user who signs in on
  // tab A and opens tab B should see the header switch to "Dashboard"
  // immediately, without having to sign in again. Likewise a cleared cache
  // that still has a valid cookie should re-hydrate silently.
  useEffect(() => {
    let cancelled = false;
    api.get("/auth/me")
      .then((res) => {
        if (cancelled) return;
        setUser(res.data);
        localStorage.setItem("psg_user", JSON.stringify(res.data));
      })
      .catch(() => {
        // 401 (session expired) or network error — clear any stale local
        // hint so the UI reflects the real (unauthenticated) state.
        if (!cancelled) {
          localStorage.removeItem("psg_user");
          setUser(null);
        }
      });
    return () => { cancelled = true; };
    // run once on mount; subsequent state changes don't need revalidation
  }, []);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      // Server has now set the httpOnly cookie automatically.
      localStorage.setItem("psg_user", JSON.stringify(res.data.user));
      setUser(res.data.user);
      return res.data.user;
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (payload) => {
    setLoading(true);
    try {
      const res = await api.post("/auth/register", payload);
      localStorage.setItem("psg_user", JSON.stringify(res.data.user));
      setUser(res.data.user);
      return res.data.user;
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
